import { Component, OnInit, Inject, ChangeDetectionStrategy } from '@angular/core';
import { ServiceordreAfregningPostDto } from 'src/app/api/models/OK/Egis/Service/Models/DTO/serviceordre-afregning-post-dto';
import { UntypedFormGroup, UntypedFormBuilder, Validators } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { EGIS_SERVICE_CALLER, MAX_INT32_VAL } from 'src/app/constants';
import { VareService } from 'src/app/api/services';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { HttpErrorResponse } from '@angular/common/http';
import { ErrorHandlerService } from 'src/app/services/error-handler.service';
import { AfregningslinieDialogData } from 'src/app/components/aftale/models';
import { NotificationService } from 'src/app/notifications/services/notification.service';
import { DoesVarenrExistsResponseDto } from 'src/app/api/models/OK/Egis/Service/G4Communication/DTO/does-varenr-exists-response-dto';

@Component({
    selector: 'app-afregningslinieeditadd',
    templateUrl: './afregningslinieeditadd.component.html',
    styleUrls: ['./afregningslinieeditadd.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class AfregningslinieeditaddComponent implements OnInit {
  form: UntypedFormGroup;
  afregningsLinje: ServiceordreAfregningPostDto;
  editMode: Boolean = false;
  isNaerVarmeLeasingGasTryghed: Boolean = false;
  vareInfoCollected: Boolean = false;

  noVareInfoInG4: Boolean = false;

  unsubscribe$ = new Subject<void>();

  get antal() {
    return this.form.get('antal');
  }
  get okKundeVarenr() {
    return this.form.get('okKundeVarenr');
  }
  get produkt() {
    return this.form.get('produkt');
  }
  get stkPrisExclMoms() {
    return this.form.get('stkPrisExclMoms');
  }
  get betaler() {
    return this.form.get('betaler');
  }

  constructor(
    private vareService: VareService,
    private errorHandler: ErrorHandlerService,
    private fb: UntypedFormBuilder,
    private dialog: MatDialogRef<AfregningslinieeditaddComponent>,
    private notifications: NotificationService,
    @Inject(MAT_DIALOG_DATA) public data: AfregningslinieDialogData
  ) {}

  ngOnInit(): void {
    this.editMode = this.data.editMode;
    this.isNaerVarmeLeasingGasTryghed = this.data.isNaerVarmeLeasingGasTryghed;

    let okKundeVarenrValidators = [Validators.required, Validators.min(0), Validators.max(MAX_INT32_VAL)];

    this.form = this.fb.group({
      antal: ['', [Validators.required, Validators.min(-999), Validators.max(999)]],
      okKundeVarenr: [null, okKundeVarenrValidators],
      produkt: ['', [Validators.required, Validators.minLength(1), Validators.maxLength(200)]],
      stkPrisExclMoms: ['', [Validators.required, Validators.min(-999999), Validators.max(999999)]],
      betaler: [1, [Validators.required]],
    });

    if (this.editMode) {
      this.initializeEdit();
    }
  }

  initializeEdit() {
    this.form.markAllAsTouched();

    this.afregningsLinje = this.data.afregningsLinje;

    this.antal.setValue(this.afregningsLinje.antal);
    this.okKundeVarenr.setValue(this.afregningsLinje.okKundeVarenr);
    this.produkt.setValue(this.afregningsLinje.produkt);
    this.stkPrisExclMoms.setValue(this.afregningsLinje.stkPrisExclMoms);
    this.betaler.setValue(this.afregningsLinje.betaler.id);
  }

  getInfoForVareNr(vareNr: number) {
    this.vareService
      .doesVareNrExist({
        caller: EGIS_SERVICE_CALLER,
        vareNr: this.okKundeVarenr.value,
        leveringsDato: this.data.leveringsDate?.toOkDateString(),
      })
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(
        (vareInfo: DoesVarenrExistsResponseDto) => {
          this.noVareInfoInG4 = !(vareInfo && vareInfo.doesVarenrExists);
          this.vareInfoCollected = !this.noVareInfoInG4;
          if (this.vareInfoCollected) {
            this.produkt.setValue(vareInfo.varetekst20);
            this.stkPrisExclMoms.setValue(vareInfo.varePris.varePrisEkslMoms);
            this.okKundeVarenr.disable();
          }
        },
        (error: HttpErrorResponse) => {
          this.vareInfoCollected = false;
          this.errorHandler.HandleError(error);
        }
      );
  }

  clearVareInfo() {
    this.vareInfoCollected = false;
    this.noVareInfoInG4 = false;
    this.okKundeVarenr.setValue(null);
    this.produkt.setValue(null);
    this.stkPrisExclMoms.setValue(null);
    this.okKundeVarenr.enable();

    //Clear validators
  }

  save() {
    this.afregningsLinje = {
      id: this.editMode ? this.data.afregningsLinje.id : null,
      antal: this.antal.value,
      okKundeVarenr: this.okKundeVarenr.value,
      produkt: this.produkt.value,
      stkPrisExclMoms: this.stkPrisExclMoms.value,
      varenrDefaultedToDiverse: this.editMode ? this.afregningsLinje.varenrDefaultedToDiverse : false,
      betaler: { id: Number(this.betaler.value) },
    };
    this.vareService
      .doesVareNrExist({
        caller: EGIS_SERVICE_CALLER,
        vareNr: this.okKundeVarenr.value,
        leveringsDato: this.data.leveringsDate?.toOkDateString(),
      })
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(
        (doesVareNrExist: DoesVarenrExistsResponseDto) => {
          if (!doesVareNrExist || !doesVareNrExist?.doesVarenrExists) {
            this.notifications.error('VareNummeret findes ikke i G4');
            return;
          }
          this.dialog.close(this.afregningsLinje);
        },
        (error: HttpErrorResponse) => {
          this.vareInfoCollected = false;
          this.errorHandler.HandleError(error);
        }
      );
  }

  cancel() {
    this.dialog.close(null);
  }
}
